export type CardType = "basic" | "basic_reversed" | "cloze" | "image_occlusion";
export type CardStatus = "proposed" | "accepted" | "rejected" | "flagged" | "duplicate";
export type Difficulty = "easy" | "medium" | "hard" | "mixed";
export type Focus =
  | "key_concepts"
  | "exam_prep"
  | "definitions"
  | "processes"
  | "memorisation"
  | "everything";
export type Grounding = "strict" | "balanced" | "open";

export interface GenerationOptions {
  card_count: number;
  difficulty: Difficulty;
  card_types: CardType[];
  focus: Focus;
  min_confidence: number;
  grounding: Grounding;
  temperature_hint: number;
  duplicate_threshold: number;
  organise_into_subdecks: boolean;
  extra_instructions: string;
}

export interface Project {
  id: string;
  name: string;
  subject: string | null;
  course: string | null;
  deck_name: string;
  tags: string[];
  options: Partial<GenerationOptions>;
  created_at: string;
  updated_at: string;
}

export interface Source {
  id: string;
  filename: string;
  kind: string;
  mime: string;
  size_bytes: number;
  status: "pending" | "processing" | "ready" | "failed";
  error: string | null;
  stats: Record<string, unknown>;
  created_at: string;
}

export interface ProjectDetail extends Project {
  sources: Source[];
  counts: {
    sources: number;
    concepts: number;
    cards_total: number;
    proposed: number;
    accepted: number;
    rejected: number;
    flagged: number;
    duplicate: number;
  };
}

export interface SourceRef {
  source_id?: string | null;
  document?: string;
  page?: number | null;
  ts_start?: number | null;
  ts_end?: number | null;
  heading?: string | null;
  chunk_id?: string | null;
}

export interface OcclusionShape {
  shape: "rect" | "ellipse" | "polygon";
  ordinal: number;
  left: number;
  top: number;
  width: number;
  height: number;
  points: [number, number][];
  label: string;
  occlude_inactive: boolean;
}

export interface QcReport {
  ok?: boolean;
  verified?: boolean;
  quality_score?: number;
  errors?: { code: string; severity: string; message: string }[];
  warnings?: { code: string; severity: string; message: string }[];
  issues?: string[];
  suggested_fix?: string;
  checks?: Record<string, boolean>;
}

export interface Card {
  id: string;
  project_id: string;
  concept_id: string | null;
  type: CardType;
  status: CardStatus;
  front: string;
  back: string;
  cloze_text: string;
  extra: string;
  header: string;
  media_id: string | null;
  occlusion_shapes: OcclusionShape[];
  tags: string[];
  topic: string | null;
  difficulty: number;
  confidence: number;
  scores: Record<string, number>;
  qc: QcReport;
  flags: string[];
  source_refs: SourceRef[];
  evidence: string;
  duplicate_of_id: string | null;
  duplicate_score: number | null;
  edited_by_user: boolean;
  created_at: string;
}

export interface JobStep {
  key: string;
  label: string;
  status: "pending" | "running" | "done" | "failed" | "skipped";
  detail: string;
}

export interface Job {
  id?: string;
  job_id?: string;
  project_id?: string;
  kind?: string;
  status: "queued" | "running" | "succeeded" | "failed" | "cancelled";
  progress: number;
  current_step: string;
  steps: JobStep[];
  result?: Record<string, any>;
  error?: ApiErrorShape | null;
}

export interface ApiErrorShape {
  code: string;
  message: string;
  hint?: string | null;
  detail?: Record<string, unknown>;
}

export interface MediaAsset {
  id: string;
  anki_filename: string;
  mime: string;
  width: number | null;
  height: number | null;
  description: string | null;
  labels: { text: string; left: number; top: number; width: number; height: number }[];
}

export interface Settings {
  llm_provider: string;
  llm_model: string;
  llm_effort: string;
  llm_key_present: boolean;
  embedding_provider: string;
  embedding_provider_active: string;
  embedding_dim: number;
  stt_provider_active: string;
  ocr_provider_active: string;
  max_upload_mb: number;
  defaults: GenerationOptions;
  grounding_strictness: string;
  retention: Record<string, unknown>;
  supported_extensions: string[];
}

export interface HealthCheck {
  ok: boolean;
  checks: { name: string; ok: boolean; detail: string; impact: string }[];
}

export interface Preflight {
  accepted: number;
  exportable_notes: number;
  anki_cards: number;
  by_type: Record<string, number>;
  blocked: { card_id: string; errors: { message: string }[] }[];
  media_referenced: number;
  requires_anki_2310: boolean;
}

export interface ExportRecord {
  id: string;
  filename: string;
  card_count: number;
  note_count: number;
  media_count: number;
  size_bytes: number;
  summary: Record<string, any>;
  created_at: string;
  download_url: string;
}

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  actions: Record<string, any>[];
  created_at: string;
}

export type Plan = "free" | "premium" | "pro";

export interface PlanLimits {
  max_projects: number | null;
  max_cards_per_generation: number;
  max_upload_mb: number;
  max_uploads_per_day: number | null;
  chatbot_enabled: boolean;
}

export interface User {
  id: string;
  email: string;
  plan: Plan;
  display_name: string | null;
}

export interface AuthMe {
  user: User;
  plan: Plan;
  limits: PlanLimits;
  usage: { projects: number; uploads_today: number; pages_today: number };
}

export const PLAN_LABEL: Record<Plan, string> = {
  free: "Free",
  premium: "Premium",
  pro: "Pro",
};
